import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { appConfig } from 'src/config';
import { withSessionExpiredHandler } from 'src/api/common/baseQueryWithAuth';

export enum GenAiChannelType {
  SMS = 'SMS',
  WHATSAPP = 'WHATSAPP',
  VIBER = 'VIBER',
  TELEGRAM = 'TELEGRAM',
}

export interface GenAiVariant {
  text: string;
}

interface GeneratedContentResult {
  customerUuid: string;
  id?: number;
  previousId?: number;
  requestId?: string;
  output: {
    channel: GenAiChannelType;
    language: string;
    tone?: string;
    max_characters: string;
    variants: GenAiVariant[];
  };
}

export interface GenerateContentPayload {
  customerUuid: string;
  prompt: string;
  language: string;
  channel: GenAiChannelType;
  tone?: string;
  numberOfVariants: number;
  length: number;
}

export interface GenerateContentResponse {
  result: GeneratedContentResult;
}

export interface EditContentPayload {
  id: number;
  customerUuid: string;
  prompt: string;
  variantNumber: number;
}

export interface EditContentResponse {
  result: GeneratedContentResult;
}

const baseUrl = appConfig.GENAI_URL;
if (!baseUrl) {
  throw new Error('REACT_APP_GENAI_URL is not configured');
}

export const genaiApi = createApi({
  reducerPath: 'genaiApi',
  baseQuery: withSessionExpiredHandler(fetchBaseQuery({
    baseUrl,
    credentials: 'include',
    prepareHeaders: (headers) => {
      if (!headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json; charset=UTF-8');
      }

      return headers;
    },
  })),
  endpoints: (builder) => ({
    generateContent: builder.mutation<GenerateContentResponse, GenerateContentPayload>({
      query: (body) => ({
        url: 'text/generate',
        method: 'POST',
        body,
      }),
    }),
    editContent: builder.mutation<EditContentResponse, EditContentPayload>({
      query: ({ id, ...body }) => ({
        url: `text/${id}/edit`,
        method: 'PUT',
        body,
      }),
    }),
  }),
});

export const { useGenerateContentMutation, useEditContentMutation } = genaiApi;
